import React from "react";
import { useTranslation } from "react-i18next";
import { RoomParticipant } from "../../../api/room";
import styles from "./RoomHeader.module.scss";

interface RoomHeaderProps {
  activeGenreNames: string[];
  filterCount: number;
  participants: RoomParticipant[];
  matchCount: number;
  onFilterClick: () => void;
  onMatchesClick: () => void;
  onInviteClick: () => void;
}

const RoomHeader: React.FC<RoomHeaderProps> = ({
  activeGenreNames,
  filterCount,
  participants,
  matchCount,
  onFilterClick,
  onMatchesClick,
  onInviteClick,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.header}>
      <div className={styles.left}>
        {/* Room title */}
        <div className={styles.titleGroup}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span className={styles.title}>{t("room_public_room", "Public Room")}</span>
        </div>

        {/* Filter tags */}
        {activeGenreNames.length > 0 && (
          <div className={styles.filterTags}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className={styles.tagIcon}>
              <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
            </svg>
            {activeGenreNames.slice(0, 3).map((name) => (
              <span key={name} className={styles.tag}>{name}</span>
            ))}
          </div>
        )}

        {/* Filter button */}
        <button className={styles.filterBtn} onClick={onFilterClick}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
          {filterCount > 0 && (
            <span className={styles.filterBadge}>{filterCount}</span>
          )}
        </button>
      </div>

      <div className={styles.right}>
        {/* Invite button */}
        <button className={styles.inviteBtn} onClick={onInviteClick}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>

        {/* Participant avatars */}
        {participants.length > 0 && (
          <div className={styles.avatars}>
            {participants.slice(0, 3).map((p, idx) => (
              <div
                key={p.user_id}
                className={styles.avatar}
                style={{ zIndex: 3 - idx }}
                title={p.username}
              >
                {p.picture_url ? (
                  <img src={p.picture_url} alt={p.username} />
                ) : (
                  <span>{(p.username || "?").charAt(0).toUpperCase()}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Matches button */}
        <button className={styles.matchesBtn} onClick={onMatchesClick}>
          {matchCount > 0 && (
            <span className={styles.matchesBadge}>{matchCount}</span>
          )}
          <span>{t("room_matches_btn", "Matches")}</span>
        </button>
      </div>
    </div>
  );
};

export default RoomHeader;
